import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchProducts, createProduct, deleteProduct, bulkCreateProducts, importProductsFromFeed, type Product } from '../services/api';

export default function Products() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showFeedModal, setShowFeedModal] = useState(false);
  const [bulkJSON, setBulkJSON] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [feedFormat, setFeedFormat] = useState('google-shopping');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    image_url: '',
    product_url: '',
    sku: '',
    category: ''
  });

  const { data, isLoading } = useQuery({
    queryKey: ['products', id],
    queryFn: () => fetchProducts(id!),
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', id] });
      setShowAddModal(false);
      resetForm();
    },
  });

  const bulkMutation = useMutation({
    mutationFn: ({ storeId, products }: { storeId: string; products: Partial<Product>[] }) =>
      bulkCreateProducts(storeId, products),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', id] });
      setShowBulkModal(false);
      setBulkJSON('');
    },
  });

  const feedMutation = useMutation({
    mutationFn: ({ storeId, feedUrl, format }: { storeId: string; feedUrl: string; format: string }) =>
      importProductsFromFeed(storeId, feedUrl, format),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products', id] });
      setShowFeedModal(false);
      setFeedUrl('');
      alert(`Successfully imported ${data.imported} products!`);
    },
    onError: (error: any) => {
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', id] });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: 'USD',
      image_url: '',
      product_url: '',
      sku: '',
      category: ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      store_id: id!,
      ...formData,
      price: formData.price ? parseFloat(formData.price) : undefined
    });
  };

  const handleBulkImport = () => {
    try {
      const products = JSON.parse(bulkJSON);
      if (!Array.isArray(products)) {
        alert('JSON must be an array of products');
        return;
      }
      bulkMutation.mutate({ storeId: id!, products });
    } catch (error) {
      alert('Invalid JSON format');
    }
  };

  const handleFeedImport = () => {
    if (!feedUrl.trim()) {
      alert('Please enter a feed URL');
      return;
    }
    feedMutation.mutate({ storeId: id!, feedUrl: feedUrl.trim(), format: feedFormat });
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading products...</div>;
  }

  const products = data?.products || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Products ({products.length})</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFeedModal(true)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            📡 Import from Feed
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            📦 Bulk Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Add Product
          </button>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No products yet</h3>
          <p className="text-gray-600 mb-6">
            Add products to enable search in your widget
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Add Your First Product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow p-4 flex gap-4">
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-24 h-24 object-cover rounded"
                />
              )}
              <div className="flex-1">
                <div className="flex justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">{product.name}</h4>
                    {product.description && (
                      <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-sm">
                      {product.price && (
                        <span className="font-semibold text-blue-600">
                          {product.currency}{product.price}
                        </span>
                      )}
                      {product.category && (
                        <span className="text-gray-500">Category: {product.category}</span>
                      )}
                      {product.sku && (
                        <span className="text-gray-500">SKU: {product.sku}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm(`Delete "${product.name}"?`)) {
                        deleteMutation.mutate(product.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-700 h-8"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add Product</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="PLN">PLN</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product URL</label>
                <input
                  type="url"
                  value={formData.product_url}
                  onChange={(e) => setFormData({ ...formData, product_url: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="/products/example"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Bulk Import Products</h3>
              <button onClick={() => setShowBulkModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Paste JSON array of products. Example format:
            </p>

            <pre className="bg-gray-50 p-3 rounded text-xs mb-4 overflow-x-auto">
{`[
  {
    "name": "Product Name",
    "description": "Product description",
    "price": 99.99,
    "currency": "USD",
    "image_url": "https://...",
    "product_url": "/products/...",
    "category": "Category",
    "sku": "SKU-001"
  }
]`}
            </pre>

            <textarea
              value={bulkJSON}
              onChange={(e) => setBulkJSON(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              rows={10}
              placeholder="Paste JSON here..."
            />

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowBulkModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkImport}
                disabled={bulkMutation.isPending}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {bulkMutation.isPending ? 'Importing...' : 'Import Products'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feed Import Modal */}
      {showFeedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Import from Product Feed</h3>
              <button onClick={() => setShowFeedModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Import products directly from your Google Shopping feed or other product feed URLs.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Feed Format</label>
                <select
                  value={feedFormat}
                  onChange={(e) => setFeedFormat(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="google-shopping">Google Shopping XML</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">More formats coming soon!</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Feed URL *</label>
                <input
                  type="url"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/products.xml"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the URL to your Google Shopping XML feed
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">ℹ️ How it works</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• We'll fetch your product feed from the URL</li>
                  <li>• Parse all products from the feed</li>
                  <li>• Import them into your store</li>
                  <li>• Duplicate products will be updated automatically</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowFeedModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFeedImport}
                disabled={feedMutation.isPending}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {feedMutation.isPending ? 'Importing...' : 'Import Products'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
